/**
 * NLQ Service — client for the Natural Language Query API.
 *
 * Provides cross-patient clinical queries with intent classification,
 * synonym expansion, hybrid retrieval, and natural language answer synthesis.
 */
import apiClient from './apiClient';
import { API_URL } from '../config';

export interface NLQEntity {
  entity_type: string;
  raw_value: string;
  canonical_value: string;
  synonyms: string[];
  snomed_code: string | null;
}

export interface NLQQueryRequest {
  query: string;
  patient_id?: string;
  study_id?: string;
  session_id?: string;
  limit?: number;
}

export interface NLQQueryResponse {
  answer: string;
  intent: string;
  source: string; // "llm" or "template"
  entities: NLQEntity[];
  count: number | null;
  exists: boolean | null;
  results: Record<string, unknown>[];
  confidence: number;
  latency_ms: number;
  query_metadata: {
    parse_method?: string;
    temporal_range?: string;
    negation?: boolean;
    comparison_mode?: boolean;
    hybrid_method?: string;
    tag_matches?: number;
    vector_matches?: number;
    distinct_patient_count?: number | null;
    hipaa_safe_harbor_applied?: boolean;
    role?: string;
    [key: string]: unknown;
  };
}

export interface NLQHealthResponse {
  status: string;
  synonym_dictionary_loaded: boolean;
  synonym_entries: number;
  categories: string[];
}

const submitQuery = async (request: NLQQueryRequest): Promise<NLQQueryResponse> => {
  const response = await apiClient.post(`${API_URL}/nlq/query`, request);
  return response.data;
};

const checkHealth = async (): Promise<NLQHealthResponse> => {
  const response = await apiClient.get(`${API_URL}/nlq/health`);
  return response.data;
};

export default { submitQuery, checkHealth };
